import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, Save, Trash2, Edit } from 'lucide-react';
import { BusinessLogoField } from '../BusinessLogoField';

/* ───── Reject reason dialog ───── */
export function RejectReasonDialog({ open, onOpenChange, reason, setReason, onConfirm, actionLoading }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#041A3B] border-white/10 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white">Reject Merchant Application</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Label className="text-[#A0B4CC] text-sm">Reason for rejection</Label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Provide a reason (will be sent to the merchant)..."
            className="w-full px-3 py-2 rounded-md bg-[#000B18] border border-white/20 text-white text-sm placeholder:text-[#5C7599] focus:border-[#FF1744] focus:outline-none resize-none"
            data-testid="reject-reason-input"
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-[#A0B4CC]">Cancel</Button>
          <Button onClick={onConfirm} disabled={actionLoading} className="bg-[#FF1744] text-white hover:bg-[#FF1744]/90" data-testid="confirm-reject-btn">
            {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm Rejection'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ───── Edit merchant dialog ───── */
export function EditMerchantDialog({ open, onOpenChange, form, setForm, industries, onSave, actionLoading, api }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#041A3B] border-white/10 max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2"><Edit className="w-5 h-5 text-[#00E5FF]" /> Edit Merchant</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
          <BusinessLogoField
            api={api}
            value={form.business_logo_url || ''}
            onChange={(url) => setForm({ ...form, business_logo_url: url })}
            testIdPrefix="admin-edit-logo"
          />
          <div>
            <Label className="text-[#A0B4CC] text-xs">Owner Name</Label>
            <Input value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#000B18] border-white/20 text-white" data-testid="edit-merchant-name" />
          </div>
          <div>
            <Label className="text-[#A0B4CC] text-xs">Email</Label>
            <Input value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#000B18] border-white/20 text-white" data-testid="edit-merchant-email" />
          </div>
          <div>
            <Label className="text-[#A0B4CC] text-xs">Mobile</Label>
            <Input value={form.mobile || ''} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="bg-[#000B18] border-white/20 text-white" placeholder="+63..." />
          </div>
          <div>
            <Label className="text-[#A0B4CC] text-xs">Business Name</Label>
            <Input value={form.business_name || ''} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="bg-[#000B18] border-white/20 text-white" data-testid="edit-merchant-business-name" />
          </div>
          <div>
            <Label className="text-[#A0B4CC] text-xs">Industry</Label>
            <select value={form.industry_id || ''} onChange={(e) => setForm({ ...form, industry_id: e.target.value })}
              className="w-full bg-[#000B18] border border-white/20 text-white rounded-md px-3 py-2 text-sm" data-testid="edit-merchant-industry">
              <option value="">Select industry...</option>
              {industries.map((ind) => <option key={ind.id} value={ind.id}>{ind.name}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[#A0B4CC] text-xs">Business Address</Label>
            <Input value={form.business_address || ''} onChange={(e) => setForm({ ...form, business_address: e.target.value })} className="bg-[#000B18] border-white/20 text-white" />
          </div>
          <div>
            <Label className="text-[#A0B4CC] text-xs">Business Description</Label>
            <textarea value={form.business_description || ''} onChange={(e) => setForm({ ...form, business_description: e.target.value })}
              rows={3} className="w-full px-3 py-2 rounded-md bg-[#000B18] border border-white/20 text-white text-sm focus:border-[#00E5FF] focus:outline-none resize-none" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-[#A0B4CC]">Cancel</Button>
          <Button onClick={onSave} disabled={actionLoading} className="bg-[#00E5FF] text-[#000B18] hover:bg-[#00E5FF]/90" data-testid="save-merchant-edit-btn">
            {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Save Changes</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ───── Delete merchant dialog ───── */
export function DeleteMerchantDialog({ open, onOpenChange, reason, setReason, onConfirm, actionLoading }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#041A3B] border-white/10 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-[#FF1744] flex items-center gap-2"><Trash2 className="w-5 h-5" /> Delete Merchant</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="p-3 rounded-lg bg-[#FF1744]/5 border border-[#FF1744]/20">
            <p className="text-[#FF1744] text-xs">This will soft-delete the merchant account and immediately deactivate all of their product/service listings from the marketplace. Data is preserved for audit.</p>
          </div>
          <Label className="text-[#A0B4CC] text-sm">Reason *</Label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Why are you deleting this merchant? (e.g., terms violation, scam reports, owner request...)"
            className="w-full px-3 py-2 rounded-md bg-[#000B18] border border-white/20 text-white text-sm placeholder:text-[#5C7599] focus:border-[#FF1744] focus:outline-none resize-none"
            data-testid="delete-reason-input"
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-[#A0B4CC]">Cancel</Button>
          <Button onClick={onConfirm} disabled={actionLoading || !reason.trim()} className="bg-[#FF1744] text-white hover:bg-[#FF1744]/90" data-testid="confirm-delete-merchant-btn">
            {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Trash2 className="w-4 h-4 mr-1.5" /> Delete Merchant</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
